import type { RouteRecordName, RouteRecordRaw, RouteRecordRedirectOption } from 'vue-router';

import {
  ADD_ONS_PERMISSION,
  canAccessSettings,
  CLIENT_SEGMENTS_PERMISSION,
  COMPANY_SETTINGS_PERMISSIONS,
  LOCATION_SETTINGS_PERMISSIONS,
  OrganizationModule,
  useAuthStore,
} from '@/entities/auth';
import { resolveChessboardTenantRedirect } from '@/features/push-navigation';
import { isPlatformHost } from '@/shared/lib';
import { isUuid } from '@/shared/lib/validators';
import { getFirstAvailableMenuTarget } from '@/widgets/main-layout';
import { PlatformLayout } from '@/widgets/platform-layout';
import { parsePlainDate } from '@shared/lib/date-time';

// Lazy-loaded Pages
const LoginPage = () => import('@/pages/auth/ui/LoginPage.vue');
const LogoutPage = () => import('@/pages/auth/ui/LogoutPage.vue');
const SetNamePage = () => import('@/pages/auth/ui/SetNamePage.vue');
const ConnectTelegramPage = () => import('@/pages/auth/ui/ConnectTelegramPage.vue');
const ConnectTelegramLinkPage = () => import('@/pages/auth/ui/ConnectTelegramLinkPage.vue');
const ConnectMaxPage = () => import('@/pages/auth/ui/ConnectMaxPage.vue');
const ConnectMaxLinkPage = () => import('@/pages/auth/ui/ConnectMaxLinkPage.vue');
const LocationSwitchPage = () => import('@/pages/location-switch').then((m) => ({ default: m.LocationSwitchPage }));
const MessengerPage = () => import('@/pages/messenger/ui/MessengerPage.vue');
const LogsPage = () => import('@/pages/logs/ui/LogsPage.vue');
const BookingChessboardPage = () => import('@/pages/booking-chessboard/ui/BookingChessboardPage.vue');
const AnalyticsPage = () => import('@/pages/analytics').then((m) => ({ default: m.AnalyticsPage }));
const AnalyticsMetricPage = () => import('@/pages/analytics').then((m) => ({ default: m.AnalyticsMetricPage }));
const NewClientRetentionReportPage = () =>
  import('@/pages/reports').then((m) => ({ default: m.NewClientRetentionReportPage }));
const RegularClientsReportPage = () => import('@/pages/reports').then((m) => ({ default: m.RegularClientsReportPage }));
const ShiftLoyaltyRegistrationsReportPage = () =>
  import('@/pages/reports').then((m) => ({ default: m.ShiftLoyaltyRegistrationsReportPage }));
const ClientsPage = () => import('@/pages/clients').then((m) => ({ default: m.ClientsPage }));
const ClientPage = () => import('@/pages/client').then((m) => ({ default: m.ClientPage }));
const PaymentsPage = () => import('@/pages/payments').then((m) => ({ default: m.PaymentsPage }));
const AddOnsPage = () => import('@/pages/add-ons').then((m) => ({ default: m.AddOnsPage }));
const RobokassaAddOnPage = () => import('@/pages/add-ons').then((m) => ({ default: m.RobokassaAddOnPage }));
const TireServiceAddOnPage = () => import('@/pages/add-ons').then((m) => ({ default: m.TireServiceAddOnPage }));
const RadioAddOnPage = () => import('@/pages/add-ons').then((m) => ({ default: m.RadioAddOnPage }));
const PaymentPage = () => import('@/pages/payment').then((m) => ({ default: m.PaymentPage }));
const ShiftsPage = () => import('@/pages/shifts').then((m) => ({ default: m.ShiftsPage }));
const ShiftPage = () => import('@/pages/shifts').then((m) => ({ default: m.ShiftPage }));
const ShopShowcasePage = () => import('@/pages/shop').then((m) => ({ default: m.ShowcasePage }));
const ShopProductsPage = () => import('@/pages/shop').then((m) => ({ default: m.ProductsPage }));
const ShopStockPage = () => import('@/pages/shop').then((m) => ({ default: m.StockPage }));
const ShopProductPage = () => import('@/pages/shop').then((m) => ({ default: m.ProductPage }));
const EmployeesPage = () => import('@/pages/employees').then((m) => ({ default: m.EmployeesPage }));
const SettingsHomePage = () => import('@/pages/settings/settings-home').then((m) => ({ default: m.SettingsHomePage }));
const BusinessSettingsPage = () =>
  import('@/pages/settings/business-settings').then((m) => ({ default: m.BusinessSettingsPage }));
const TireWorkOrdersPage = () => import('@/pages/tire-service').then((m) => ({ default: m.WorkOrdersPage }));
const TireWorkOrderPage = () => import('@/pages/tire-service').then((m) => ({ default: m.WorkOrderPage }));
const TireStoragesPage = () => import('@/pages/tire-service').then((m) => ({ default: m.StoragesPage }));
const TireStoragePage = () => import('@/pages/tire-service').then((m) => ({ default: m.StoragePage }));
// Печатные формы открываются отдельной вкладкой: грузим только их, без остальных страниц шиномонтажа.
const TireWorkOrderPrintPage = () => import('@/pages/tire-service/ui/print/WorkOrderPrintPage.vue');
const TireStorageContractPrintPage = () => import('@/pages/tire-service/ui/print/StorageContractPrintPage.vue');
const TireStorageReleasePrintPage = () => import('@/pages/tire-service/ui/print/StorageReleasePrintPage.vue');
const TireStorageLabelsPrintPage = () => import('@/pages/tire-service/ui/print/StorageLabelsPrintPage.vue');
const ServicesSettingsPage = () =>
  import('@/pages/settings/services').then((m) => ({ default: m.ServicesSettingsPage }));
const TireServiceSettingsPage = () =>
  import('@/pages/settings/tire-service-settings').then((m) => ({ default: m.TireServiceSettingsPage }));
const ClientSegmentsPage = () =>
  import('@/pages/settings/client-settings').then((m) => ({ default: m.ClientSegmentsPage }));
const LoyaltySettingsPage = () =>
  import('@/pages/settings/loyalty-settings').then((m) => ({ default: m.LoyaltySettingsPage }));
const RadioPage = () => import('@/pages/radio/ui/RadioPage.vue');
const RadioManagePage = () => import('@/pages/radio/ui/RadioManagePage.vue');
const SopSectionsPage = () => import('@/pages/sop/ui/SopSectionsPage.vue');
const SopSectionDocumentsPage = () => import('@/pages/sop/ui/SopSectionDocumentsPage.vue');
const SopViewPage = () => import('@/pages/sop/ui/SopViewPage.vue');
const SopEditorPage = () => import('@/pages/sop/ui/SopEditorPage.vue');
const AutomationPage = () => import('@/pages/automation').then((m) => ({ default: m.AutomationPage }));
const ScenariosPage = () => import('@/pages/scenarios').then((m) => ({ default: m.ScenariosPage }));
const ScenarioEditPage = () => import('@/pages/scenario/ui/ScenarioEditPage.vue');
const ScenarioViewPage = () => import('@/pages/scenario/ui/ScenarioViewPage.vue');
const MarketingSourcesPage = () =>
  import('@/pages/marketing-sources').then((m) => ({ default: m.MarketingSourcesPage }));
const MarketingSourcePage = () => import('@/pages/marketing-source').then((m) => ({ default: m.MarketingSourcePage }));
const BroadcastsPage = () => import('@/pages/broadcasts').then((m) => ({ default: m.BroadcastsPage }));
const BroadcastPage = () => import('@/pages/broadcast').then((m) => ({ default: m.BroadcastPage }));
const QrLinksPage = () => import('@/pages/qr-links').then((m) => ({ default: m.QrLinksPage }));
const QrLinkEditPage = () => import('@/pages/qr-link').then((m) => ({ default: m.QrLinkEditPage }));
const InstallPage = () => import('@/pages/install').then((m) => ({ default: m.InstallPage }));
const PushNotificationsPage = () =>
  import('@/pages/push-notifications').then((m) => ({ default: m.PushNotificationsPage }));
const PushBookingPage = () => import('@/pages/push-booking').then((m) => ({ default: m.PushBookingPage }));
const PushMessengerPage = () => import('@/pages/push-messenger').then((m) => ({ default: m.PushMessengerPage }));
const PlatformOverviewPage = () =>
  import('@/pages/platform-overview').then((m) => ({ default: m.PlatformOverviewPage }));
const PlatformOrganizationsPage = () =>
  import('@/pages/platform-organizations').then((m) => ({ default: m.PlatformOrganizationsPage }));
const PlatformAccessDeniedPage = () =>
  import('@/pages/platform-access-denied').then((m) => ({ default: m.PlatformAccessDeniedPage }));
const PlatformOrganizationPage = () =>
  import('@/pages/platform-organization').then((m) => ({ default: m.PlatformOrganizationPage }));
const PlatformUserPage = () => import('@/pages/platform-user').then((m) => ({ default: m.PlatformUserPage }));
const PlatformUsersPage = () => import('@/pages/platform-users').then((m) => ({ default: m.PlatformUsersPage }));
const PlatformAuditPage = () => import('@/pages/platform-audit').then((m) => ({ default: m.PlatformAuditPage }));
const ImpersonationHandoffPage = () =>
  import('@/pages/impersonation-handoff').then((m) => ({ default: m.ImpersonationHandoffPage }));

const HomeRedirectBoundary = { render: () => null };
const platformRouteMeta = {
  requiresAuth: true,
  requiresOrganization: false,
  superuser: true,
} as const;
// Модуль не указан намеренно: клиентские автоматизации уходят у всех организаций,
// доступ определяет право (см. серверный гейт без @module).
const automationRouteMeta = {
  requiresAuth: true,
  requiresOrganization: true,
  title: 'Автоматизация',
  permissions: ['client_automations.manage'],
};
const clientSegmentsRouteMeta = {
  navigationSection: 'settings',
  requiresAuth: true,
  requiresOrganization: true,
  title: 'Сегменты клиентов',
  modules: [OrganizationModule.Clients],
  permissions: [CLIENT_SEGMENTS_PERMISSION],
};
const addOnsRouteMeta = {
  navigationSection: 'settings',
  requiresAuth: true,
  requiresOrganization: true,
  permissions: [ADD_ONS_PERMISSION],
};

/** Раздел открыт и при выключенном хранении: на нём хранение и включают. */
const tireServiceSettingsRouteMeta = {
  navigationSection: 'settings',
  requiresAuth: true,
  requiresOrganization: true,
  locationOptional: true,
  modules: [OrganizationModule.TireService],
  permissions: ['tire_service.settings'],
};

const createLegacyPlatformRedirect =
  (name: RouteRecordName): RouteRecordRedirectOption =>
  (to) => ({
    name,
    params: to.params,
    query: to.query,
    hash: to.hash,
  });

/** Старые URL настроек ведут в секцию рабочей локации, а без неё — в список локаций. */
const workingLocationSection =
  (section: string): RouteRecordRedirectOption =>
  () => {
    const locationId = useAuthStore().currentLocation?.id;
    return locationId
      ? { name: 'business-settings.locations.detail', params: { locationId, section } }
      : { name: 'business-settings.locations' };
  };

export const resolveHomeRedirect = (platformHost: boolean) => {
  const authStore = useAuthStore();

  if (platformHost) {
    return authStore.user?.is_super_user ? { name: 'platform.overview' } : { name: 'auth.platform-access-denied' };
  }

  if (authStore.currentOrganization) {
    const target = getFirstAvailableMenuTarget({
      hasModule: (module) => authStore.hasModule(module),
      hasPermission: (permission) => authStore.hasPermission(permission),
      hasAnyPermission: (permissions) => authStore.hasAnyPermission(permissions),
      hasLocation: Boolean(authStore.currentLocation),
    });

    return target ?? { name: 'auth.location-switch' };
  }

  return { name: 'auth.location-switch' };
};

/**
 * Определение всех маршрутов приложения
 */
export const routes: RouteRecordRaw[] = [
  // NotFound - редирект на главную
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    redirect: '/',
  },

  // Home
  {
    path: '/',
    name: 'home.index',
    component: HomeRedirectBoundary,
    beforeEnter: () => resolveHomeRedirect(isPlatformHost(globalThis.window?.location.origin ?? '')),
    meta: {
      requiresOrganization: false,
    },
  },

  {
    path: '/access-denied',
    name: 'auth.platform-access-denied',
    component: PlatformAccessDeniedPage,
    meta: {
      sidebar: false,
      requiresAuth: true,
      requiresOrganization: false,
    },
  },

  // Redirects keep existing bookmarks valid after removing the redundant
  // /platform prefix from the dedicated platform host.
  {
    path: '/platform',
    redirect: createLegacyPlatformRedirect('platform.overview'),
  },
  {
    path: '/platform/access-denied',
    redirect: createLegacyPlatformRedirect('auth.platform-access-denied'),
  },
  {
    path: '/platform/organizations',
    redirect: createLegacyPlatformRedirect('platform.organizations'),
  },
  {
    path: '/platform/organizations/:id',
    redirect: createLegacyPlatformRedirect('platform.organizations.show'),
  },
  {
    path: '/platform/users',
    redirect: createLegacyPlatformRedirect('platform.users'),
  },
  {
    path: '/platform/users/:id',
    redirect: createLegacyPlatformRedirect('platform.users.show'),
  },
  {
    path: '/platform/audit',
    redirect: createLegacyPlatformRedirect('platform.audit'),
  },

  // Platform console
  // meta задаётся только на родителе: vue-router сливает meta всех совпавших
  // записей, поэтому guard-ы видят platformRouteMeta и на дочерних маршрутах.
  {
    path: '/overview',
    component: PlatformLayout,
    meta: platformRouteMeta,
    children: [
      {
        path: '',
        name: 'platform.overview',
        component: PlatformOverviewPage,
      },
    ],
  },
  {
    path: '/organizations',
    component: PlatformLayout,
    meta: platformRouteMeta,
    children: [
      {
        path: '',
        name: 'platform.organizations',
        component: PlatformOrganizationsPage,
      },
      {
        path: ':id',
        name: 'platform.organizations.show',
        component: PlatformOrganizationPage,
      },
    ],
  },
  {
    path: '/users',
    component: PlatformLayout,
    meta: platformRouteMeta,
    children: [
      {
        path: '',
        name: 'platform.users',
        component: PlatformUsersPage,
      },
      {
        path: ':id',
        name: 'platform.users.show',
        component: PlatformUserPage,
      },
    ],
  },
  {
    path: '/audit',
    component: PlatformLayout,
    meta: platformRouteMeta,
    children: [
      {
        path: '',
        name: 'platform.audit',
        component: PlatformAuditPage,
      },
    ],
  },

  // Public PWA installation guide
  {
    path: '/install-app',
    name: 'install.index',
    component: InstallPage,
    meta: {
      sidebar: false,
      requiresAuth: false,
      requiresOrganization: false,
    },
  },

  // User/device-scoped Web Push controls
  {
    path: '/push-notifications',
    name: 'push-notifications.index',
    component: PushNotificationsPage,
    meta: {
      sidebar: false,
      requiresAuth: true,
      requiresOrganization: false,
      title: 'Уведомления',
    },
  },

  // Validates a privacy-safe push payload, switches tenant and opens the day.
  {
    path: '/push/booking/:date',
    name: 'push.booking',
    component: PushBookingPage,
    meta: {
      sidebar: false,
      requiresAuth: true,
      requiresOrganization: false,
    },
  },

  // Validates a messenger push payload, switches tenant and opens the conversation.
  {
    path: '/push/messenger/:conversationId',
    name: 'push.messenger',
    component: PushMessengerPage,
    meta: {
      sidebar: false,
      requiresAuth: true,
      requiresOrganization: false,
    },
  },

  // Auth
  {
    path: '/auth/impersonation-handoff',
    name: 'auth.impersonation-handoff',
    component: ImpersonationHandoffPage,
    meta: {
      sidebar: false,
      requiresAuth: false,
      requiresOrganization: false,
    },
  },
  {
    path: '/auth/login',
    name: 'auth.login',
    component: LoginPage,
    meta: {
      sidebar: false,
      requiresAuth: false,
      requiresOrganization: false,
      onlyGuest: true,
    },
  },
  {
    path: '/auth/logout',
    name: 'auth.logout',
    component: LogoutPage,
    meta: {
      sidebar: false,
      requiresAuth: true,
      requiresOrganization: false,
    },
  },
  {
    path: '/auth/set-name',
    name: 'auth.set-name',
    component: SetNamePage,
    meta: {
      sidebar: false,
      requiresAuth: true,
      requiresOrganization: false,
    },
  },
  {
    path: '/auth/connect/telegram',
    name: 'auth.connect-telegram',
    component: ConnectTelegramPage,
    meta: {
      sidebar: false,
      requiresAuth: true,
      requiresOrganization: false,
    },
  },
  {
    path: '/auth/connect/telegram/link',
    name: 'auth.connect-telegram-link',
    component: ConnectTelegramLinkPage,
    meta: {
      sidebar: false,
      requiresAuth: true,
      requiresOrganization: false,
    },
  },
  {
    path: '/auth/connect/max',
    name: 'auth.connect-max',
    component: ConnectMaxPage,
    meta: {
      sidebar: false,
      requiresAuth: true,
      requiresOrganization: false,
    },
  },
  {
    path: '/auth/connect/max/link',
    name: 'auth.connect-max-link',
    component: ConnectMaxLinkPage,
    meta: {
      sidebar: false,
      requiresAuth: true,
      requiresOrganization: false,
    },
  },
  {
    path: '/auth/location-switch',
    name: 'auth.location-switch',
    component: LocationSwitchPage,
    meta: {
      sidebar: false,
      requiresAuth: true,
      requiresOrganization: false,
    },
  },

  // Messenger
  {
    path: '/messenger',
    name: 'messenger.index',
    component: MessengerPage,
    meta: {
      sidebar: true,
      modules: [OrganizationModule.Messenger],
      permissions: ['messenger.view'],
    },
  },

  // Logs
  {
    path: '/logs',
    name: 'logs.index',
    component: LogsPage,
  },

  // Booking Chessboard
  {
    path: '/booking-chessboard/:date/:bookingId?',
    name: 'booking.chessboard',
    component: BookingChessboardPage,
    props(route) {
      return {
        selectedDate: parsePlainDate(route.params.date),
        bookingId: isUuid(route.params.bookingId) ? route.params.bookingId : null,
      };
    },
    beforeEnter: (to) => {
      const authStore = useAuthStore();

      return resolveChessboardTenantRedirect(to, {
        organizationId: authStore.currentOrganization?.id ?? null,
        locationId: authStore.currentLocation?.id ?? null,
      });
    },
    meta: {
      sidebar: true,
      modules: [OrganizationModule.Booking],
    },
  },

  // Analytics overview
  {
    path: '/analytics',
    name: 'analytics.index',
    component: AnalyticsPage,
    meta: {
      sidebar: true,
      requiresAuth: true,
      requiresOrganization: true,
      title: 'Отчёты',
      modules: [OrganizationModule.Analytics],
      permissions: ['analytics.view'],
    },
  },
  {
    path: '/analytics/:metric',
    name: 'analytics.metric',
    component: AnalyticsMetricPage,
    meta: {
      sidebar: true,
      requiresAuth: true,
      requiresOrganization: true,
      title: 'Отчёты',
      modules: [OrganizationModule.Analytics],
      permissions: ['analytics.view'],
    },
  },

  // Reports
  {
    path: '/reports/loyalty-registrations',
    name: 'reports.loyalty-registrations',
    component: ShiftLoyaltyRegistrationsReportPage,
    meta: {
      sidebar: true,
      requiresAuth: true,
      requiresOrganization: true,
      title: 'Отчёты',
      permissions: ['reports.view'],
    },
  },

  {
    path: '/reports/new-client-retention',
    name: 'reports.new-client-retention',
    component: NewClientRetentionReportPage,
    meta: {
      sidebar: true,
      requiresAuth: true,
      requiresOrganization: true,
      title: 'Отчёты',
      permissions: ['analytics.view'],
    },
  },

  {
    path: '/reports/regular-clients',
    name: 'reports.regular-clients',
    component: RegularClientsReportPage,
    meta: {
      sidebar: true,
      requiresAuth: true,
      requiresOrganization: true,
      title: 'Отчёты',
      permissions: ['analytics.view'],
    },
  },

  // Clients (GraphQL)
  {
    path: '/clients',
    name: 'clients.index',
    component: ClientsPage,
    meta: {
      requiresAuth: true,
      title: 'Клиенты',
      modules: [OrganizationModule.Clients],
      permissions: ['clients.detail'],
    },
  },
  {
    path: '/clients/:id',
    name: 'clients.show',
    component: ClientPage,
    meta: {
      requiresAuth: true,
      modules: [OrganizationModule.Clients],
      permissions: ['clients.detail'],
      title: 'Клиент',
    },
  },

  // Orders
  {
    path: '/payments',
    name: 'payments.index',
    component: PaymentsPage,
    meta: {
      requiresAuth: true,
      requiresOrganization: true,
      permissions: ['payments.view', 'payments.accept', 'fiscalization.manage'],
      title: 'Платежи',
    },
  },
  // Карточка одна: по платежу или по заказу без платежа.
  {
    path: '/payments/orders/:orderId',
    name: 'payments.order',
    component: PaymentPage,
    meta: {
      sidebar: true,
      requiresAuth: true,
      requiresOrganization: true,
      title: 'Платёж',
      permissions: ['payments.view'],
    },
  },
  {
    path: '/payments/:id',
    name: 'payments.show',
    component: PaymentPage,
    meta: {
      sidebar: true,
      requiresAuth: true,
      requiresOrganization: true,
      title: 'Платёж',
      permissions: ['payments.view', 'payments.accept', 'fiscalization.manage'],
    },
  },
  {
    path: '/payments/operations/:id',
    redirect: (to) => ({ name: 'payments.show', params: { id: to.params.id } }),
  },
  {
    path: '/shifts',
    name: 'shifts.index',
    component: ShiftsPage,
    meta: {
      sidebar: true,
      requiresAuth: true,
      requiresOrganization: true,
      title: 'Смены',
      modules: [OrganizationModule.Orders],
      permissions: ['payments.view'],
    },
  },
  {
    path: '/shifts/:id',
    name: 'shifts.show',
    component: ShiftPage,
    meta: {
      sidebar: true,
      requiresAuth: true,
      requiresOrganization: true,
      title: 'Смена',
      modules: [OrganizationModule.Orders],
      permissions: ['payments.view'],
    },
  },

  // Шиномонтаж
  {
    path: '/tire-service/work-orders',
    name: 'tire-service.work-orders',
    component: TireWorkOrdersPage,
    meta: {
      sidebar: true,
      requiresAuth: true,
      requiresOrganization: true,
      title: 'Заказ-наряды',
      modules: [OrganizationModule.TireService],
      permissions: ['tire_service.view'],
    },
  },
  {
    path: '/tire-service/work-orders/new',
    name: 'tire-service.work-order-new',
    component: TireWorkOrderPage,
    meta: {
      sidebar: true,
      requiresAuth: true,
      requiresOrganization: true,
      title: 'Заказ-наряд',
      modules: [OrganizationModule.TireService],
      permissions: ['tire_service.manage'],
    },
  },
  {
    path: '/tire-service/work-orders/:id',
    name: 'tire-service.work-order',
    component: TireWorkOrderPage,
    meta: {
      sidebar: true,
      requiresAuth: true,
      requiresOrganization: true,
      title: 'Заказ-наряд',
      modules: [OrganizationModule.TireService],
      permissions: ['tire_service.view'],
    },
  },
  {
    path: '/tire-service/storages',
    name: 'tire-service.storages',
    component: TireStoragesPage,
    meta: {
      sidebar: true,
      requiresAuth: true,
      requiresOrganization: true,
      title: 'Хранение',
      modules: [OrganizationModule.TireStorage],
      permissions: ['tire_service.view'],
    },
  },
  {
    path: '/tire-service/storages/:id',
    name: 'tire-service.storage',
    component: TireStoragePage,
    meta: {
      sidebar: true,
      requiresAuth: true,
      requiresOrganization: true,
      title: 'Хранение',
      modules: [OrganizationModule.TireStorage],
      permissions: ['tire_service.view'],
    },
  },
  // Печатные формы открываются в новой вкладке без каркаса админки.
  {
    path: '/tire-service/work-orders/:id/print',
    name: 'tire-service.work-order-print',
    component: TireWorkOrderPrintPage,
    meta: {
      requiresAuth: true,
      requiresOrganization: true,
      title: 'Заказ-наряд',
      modules: [OrganizationModule.TireService],
      permissions: ['tire_service.view'],
    },
  },
  {
    path: '/tire-service/storages/:id/contract',
    name: 'tire-service.storage-contract-print',
    component: TireStorageContractPrintPage,
    meta: {
      requiresAuth: true,
      requiresOrganization: true,
      title: 'Договор хранения',
      modules: [OrganizationModule.TireStorage],
      permissions: ['tire_service.view'],
    },
  },
  {
    path: '/tire-service/storages/:id/releases/:releaseId/act',
    name: 'tire-service.storage-release-print',
    component: TireStorageReleasePrintPage,
    meta: {
      requiresAuth: true,
      requiresOrganization: true,
      title: 'Акт выдачи',
      modules: [OrganizationModule.TireStorage],
      permissions: ['tire_service.view'],
    },
  },
  {
    path: '/tire-service/storages/:id/labels',
    name: 'tire-service.storage-labels-print',
    component: TireStorageLabelsPrintPage,
    meta: {
      requiresAuth: true,
      requiresOrganization: true,
      title: 'Наклейки хранения',
      modules: [OrganizationModule.TireStorage],
      permissions: ['tire_service.view'],
    },
  },

  // Shop
  {
    path: '/shop',
    name: 'shop.showcase',
    component: ShopShowcasePage,
    meta: {
      sidebar: true,
      requiresAuth: true,
      requiresOrganization: true,
      title: 'Витрина',
      modules: [OrganizationModule.Shop],
      permissions: ['payments.accept'],
    },
  },
  {
    path: '/shop/stock',
    name: 'shop.stock',
    component: ShopStockPage,
    meta: {
      sidebar: true,
      requiresAuth: true,
      requiresOrganization: true,
      title: 'Склад',
      modules: [OrganizationModule.Shop],
      permissions: ['shop.receive'],
      // Карточка товара и операция открываются поверх того же списка.
      keepScrollOnQueryChange: true,
    },
  },
  {
    path: '/shop/products',
    name: 'shop.products.index',
    component: ShopProductsPage,
    meta: {
      sidebar: true,
      requiresAuth: true,
      requiresOrganization: true,
      title: 'Каталог',
      modules: [OrganizationModule.Shop],
      permissions: ['shop.view'],
    },
  },
  {
    path: '/shop/products/:id',
    name: 'shop.products.show',
    component: ShopProductPage,
    meta: {
      sidebar: true,
      requiresAuth: true,
      requiresOrganization: true,
      title: 'Товар',
      modules: [OrganizationModule.Shop],
      permissions: ['shop.view'],
    },
  },

  {
    path: '/settings',
    name: 'settings.index',
    component: SettingsHomePage,
    beforeEnter: () => (canAccessSettings(useAuthStore()) ? true : { name: 'home.index' }),
    meta: {
      requiresAuth: true,
      requiresOrganization: true,
      title: 'Настройки',
      navigationSection: 'settings',
      locationOptional: true,
    },
  },

  // Employees (GraphQL)
  {
    path: '/employees',
    name: 'employees.index',
    component: EmployeesPage,
    meta: {
      navigationSection: 'settings',
      requiresAuth: true,
      title: 'Сотрудники',
      permissions: ['employees.manage'],
    },
  },

  {
    path: '/business-settings/company',
    name: 'business-settings.company',
    component: BusinessSettingsPage,
    meta: {
      navigationSection: 'settings',
      requiresAuth: true,
      requiresOrganization: true,
      title: 'Компания',
      locationOptional: true,
      permissions: COMPANY_SETTINGS_PERMISSIONS,
    },
  },
  {
    path: '/business-settings/locations',
    name: 'business-settings.locations',
    component: BusinessSettingsPage,
    meta: {
      navigationSection: 'settings',
      requiresAuth: true,
      requiresOrganization: true,
      title: 'Локации',
      locationOptional: true,
      permissions: LOCATION_SETTINGS_PERMISSIONS,
    },
  },
  {
    path: '/business-settings/locations/:locationId/:section?',
    name: 'business-settings.locations.detail',
    component: BusinessSettingsPage,
    meta: {
      navigationSection: 'settings',
      requiresAuth: true,
      requiresOrganization: true,
      title: 'Настройки локации',
      locationOptional: true,
      permissions: LOCATION_SETTINGS_PERMISSIONS,
    },
  },
  {
    path: '/location-settings',
    name: 'location-settings.index',
    redirect: workingLocationSection('hours'),
  },
  {
    path: '/client-settings/segments',
    name: 'client-settings.segments',
    component: ClientSegmentsPage,
    meta: clientSegmentsRouteMeta,
  },
  // Редактор — слой поверх того же списка: страница не пересоздаётся, Back/Forward закрывают и открывают его.
  {
    path: '/client-settings/segments/new',
    name: 'client-settings.segments.create',
    component: ClientSegmentsPage,
    meta: { ...clientSegmentsRouteMeta, title: 'Новый сегмент' },
  },
  {
    path: '/client-settings/segments/:id',
    name: 'client-settings.segments.edit',
    component: ClientSegmentsPage,
    meta: { ...clientSegmentsRouteMeta, title: 'Изменение сегмента' },
  },
  {
    path: '/loyalty-settings',
    name: 'loyalty-settings.index',
    component: LoyaltySettingsPage,
    meta: {
      navigationSection: 'settings',
      requiresAuth: true,
      requiresOrganization: true,
      title: 'Программа лояльности',
      modules: [OrganizationModule.Loyalty],
      permissions: ['loyalty.manage'],
    },
  },
  {
    path: '/settings/services',
    name: 'settings.services',
    component: ServicesSettingsPage,
    meta: {
      navigationSection: 'settings',
      requiresAuth: true,
      requiresOrganization: true,
      title: 'Услуги',
      locationOptional: true,
      permissions: ['catalog.manage'],
    },
  },
  {
    path: '/settings/tire-service',
    name: 'settings.tire-service',
    redirect: { name: 'settings.tire-service.price' },
  },
  {
    path: '/settings/tire-service/price',
    name: 'settings.tire-service.price',
    component: TireServiceSettingsPage,
    meta: { ...tireServiceSettingsRouteMeta, title: 'Классы и диаметры' },
  },
  {
    path: '/settings/tire-service/warehouses',
    name: 'settings.tire-service.warehouses',
    component: TireServiceSettingsPage,
    meta: { ...tireServiceSettingsRouteMeta, title: 'Склады хранения' },
  },
  {
    path: '/settings/add-ons',
    name: 'settings.add-ons',
    component: AddOnsPage,
    meta: { ...addOnsRouteMeta, title: 'Дополнения' },
  },
  {
    path: '/settings/add-ons/robokassa',
    name: 'settings.add-ons.robokassa',
    component: RobokassaAddOnPage,
    meta: { ...addOnsRouteMeta, title: 'Robokassa' },
  },
  {
    path: '/settings/add-ons/tire-service',
    name: 'settings.add-ons.tire-service',
    component: TireServiceAddOnPage,
    meta: { ...addOnsRouteMeta, title: 'Шиномонтаж', permissions: [ADD_ONS_PERMISSION, 'tire_service.settings'] },
  },
  {
    path: '/settings/add-ons/radio',
    name: 'settings.add-ons.radio',
    component: RadioAddOnPage,
    meta: { ...addOnsRouteMeta, title: 'Радио' },
  },
  {
    path: '/fiscalization-settings',
    name: 'fiscalization-settings.index',
    redirect: workingLocationSection('orders'),
  },
  // Переходный redirect: старый URL страницы (открытые вкладки/закладки)
  {
    path: '/booking-settings',
    redirect: workingLocationSection('hours'),
  },

  // Радио
  {
    path: '/radio',
    name: 'radio.index',
    component: RadioPage,
    meta: {
      requiresAuth: true,
      title: 'Радио',
      modules: [OrganizationModule.Radio],
    },
  },
  {
    path: '/radio/manage',
    name: 'radio.manage',
    component: RadioManagePage,
    meta: {
      requiresAuth: true,
      title: 'Управление',
      modules: [OrganizationModule.Radio],
      permissions: ['radio.manage'],
    },
  },
  // Переходный redirect: прежний адрес раздела в закладках устройств в залах
  {
    path: '/soundflow',
    redirect: { name: 'radio.index' },
  },
  {
    path: '/soundflow/manage',
    redirect: { name: 'radio.manage' },
  },

  // SOP
  {
    path: '/sop',
    name: 'sop.sections',
    component: SopSectionsPage,
    meta: {
      requiresAuth: true,
      title: 'Разделы',
      modules: [OrganizationModule.Sop],
      permissions: ['sop.view'],
    },
  },
  {
    path: '/sop/section/:sectionId',
    name: 'sop.section',
    component: SopSectionDocumentsPage,
    meta: {
      requiresAuth: true,
      title: 'Документы раздела',
      modules: [OrganizationModule.Sop],
      permissions: ['sop.view'],
    },
  },
  {
    path: '/sop/create',
    name: 'sop.create',
    component: SopEditorPage,
    meta: {
      requiresAuth: true,
      title: 'Создать регламент',
      modules: [OrganizationModule.Sop],
      permissions: ['sop.manage'],
    },
  },
  {
    path: '/sop/:id',
    name: 'sop.view',
    component: SopViewPage,
    meta: {
      requiresAuth: true,
      title: 'Просмотр регламента',
      modules: [OrganizationModule.Sop],
      permissions: ['sop.view'],
    },
  },
  {
    path: '/sop/:id/edit',
    name: 'sop.edit',
    component: SopEditorPage,
    meta: {
      requiresAuth: true,
      title: 'Редактировать регламент',
      modules: [OrganizationModule.Sop],
      permissions: ['sop.manage'],
    },
  },

  // Client automations
  {
    path: '/automation',
    name: 'automation.index',
    component: AutomationPage,
    meta: { sidebar: true, ...automationRouteMeta },
  },
  {
    path: '/automation/:kind/new',
    name: 'automation.create',
    component: AutomationPage,
    meta: { ...automationRouteMeta, title: 'Новая автоматизация' },
  },
  // Встроенная карточка вида — без id, созданная — со своим id.
  {
    path: '/automation/:kind/:id?',
    name: 'automation.edit',
    component: AutomationPage,
    meta: { ...automationRouteMeta, title: 'Настройка автоматизации' },
  },

  // Scenarios
  {
    path: '/scenarios',
    name: 'scenarios.index',
    component: ScenariosPage,
    meta: {
      sidebar: true,
      requiresAuth: true,
      requiresOrganization: true,
      title: 'Сценарии',
      modules: [OrganizationModule.Marketing],
      permissions: ['scenario.view'],
    },
  },
  {
    path: '/scenarios/new',
    name: 'scenarios.create',
    component: ScenarioEditPage,
    meta: {
      requiresAuth: true,
      requiresOrganization: true,
      title: 'Новый сценарий',
      modules: [OrganizationModule.Marketing],
      permissions: ['scenario.create'],
    },
  },
  {
    path: '/scenarios/:id',
    name: 'scenarios.show',
    component: ScenarioViewPage,
    meta: {
      requiresAuth: true,
      requiresOrganization: true,
      title: 'Сценарий',
      modules: [OrganizationModule.Marketing],
      permissions: ['scenario.view'],
    },
  },
  {
    path: '/scenarios/:id/edit',
    name: 'scenarios.edit',
    component: ScenarioEditPage,
    meta: {
      requiresAuth: true,
      requiresOrganization: true,
      title: 'Редактирование сценария',
      modules: [OrganizationModule.Marketing],
      permissions: ['scenario.update'],
    },
  },

  // Marketing sources
  {
    path: '/marketing-sources',
    name: 'marketing-sources.index',
    component: MarketingSourcesPage,
    meta: {
      sidebar: true,
      requiresAuth: true,
      requiresOrganization: true,
      title: 'Каналы привлечения',
      modules: [OrganizationModule.Marketing],
      permissions: ['marketing_source.view'],
    },
  },
  {
    path: '/marketing-sources/new',
    name: 'marketing-sources.create',
    component: MarketingSourcePage,
    meta: {
      requiresAuth: true,
      requiresOrganization: true,
      title: 'Новый канал',
      modules: [OrganizationModule.Marketing],
      permissions: ['marketing_source.create'],
    },
  },
  {
    path: '/marketing-sources/:id',
    name: 'marketing-sources.edit',
    component: MarketingSourcePage,
    meta: {
      requiresAuth: true,
      requiresOrganization: true,
      title: 'Редактирование канала',
      modules: [OrganizationModule.Marketing],
      permissions: ['marketing_source.update'],
    },
  },

  // QR Links
  {
    path: '/marketing/broadcasts',
    name: 'broadcasts.index',
    component: BroadcastsPage,
    meta: {
      requiresAuth: true,
      requiresOrganization: true,
      title: 'Рассылки',
      modules: [OrganizationModule.Marketing],
      permissions: ['marketing.broadcasts.view'],
    },
  },
  {
    path: '/marketing/broadcasts/new',
    name: 'broadcasts.create',
    component: BroadcastPage,
    meta: {
      requiresAuth: true,
      requiresOrganization: true,
      title: 'Новая рассылка',
      modules: [OrganizationModule.Marketing],
      permissions: ['marketing.broadcasts.manage'],
    },
  },
  {
    path: '/marketing/broadcasts/:id',
    name: 'broadcasts.show',
    component: BroadcastPage,
    meta: {
      requiresAuth: true,
      requiresOrganization: true,
      title: 'Рассылка',
      modules: [OrganizationModule.Marketing],
      permissions: ['marketing.broadcasts.view'],
    },
  },
  {
    path: '/marketing/qr-links',
    name: 'qr-links.index',
    component: QrLinksPage,
    meta: {
      sidebar: true,
      requiresAuth: true,
      requiresOrganization: true,
      title: 'QR-коды',
      modules: [OrganizationModule.Marketing],
      permissions: ['qr_link.view'],
    },
  },
  {
    path: '/marketing/qr-links/new',
    name: 'qr-links.create',
    component: QrLinkEditPage,
    meta: {
      requiresAuth: true,
      requiresOrganization: true,
      title: 'Новый QR-код',
      modules: [OrganizationModule.Marketing],
      permissions: ['qr_link.create'],
    },
  },
  {
    path: '/marketing/qr-links/:id',
    name: 'qr-links.edit',
    component: QrLinkEditPage,
    meta: {
      requiresAuth: true,
      requiresOrganization: true,
      title: 'Редактирование QR-кода',
      modules: [OrganizationModule.Marketing],
      permissions: ['qr_link.view'],
    },
  },
];
